import _ from 'underscore'
import React from 'react'
import PropTypes from 'prop-types'

import Form from '../elements/Form'
import StaticInput from '../elements/forms/StaticInput'
import TextInput from '../elements/forms/TextInput'
import IntegerInput from '../elements/forms/IntegerInput'
import CheckboxInput from '../elements/forms/CheckboxInput'
import LocationSelect from '../elements/forms/LocationSelect'
import Dismissible from '../elements/Dismissible'
import AppUtils from '../../utils/AppUtils'

const ERROR = {
  EMPTY_QUANTITY: 'Minimum 1.',
  EMPTY_LOCATION: 'Veuillez sélectionner un emplacement',
  EMPTY_NUMBER: 'Le N° ne peut pas être vide'
}

class ItemForm extends React.Component {

  constructor(props) {

    // Properties
    super(props)

    // State
    this.state = {
      input: this.getInitialInput(props),
      formErrors: []
    }

    // Bind events
    this.onChangeInputQuantity = this.onChangeInputQuantity.bind(this)
    this.onChangeInputLocation = this.onChangeInputLocation.bind(this)
    this.onChangeCheckboxIsLabeled = this.onChangeCheckboxIsLabeled.bind(this)
    this.onChangeInputCompartment = this.onChangeInputCompartment.bind(this)
    this.onChangeInputNumber = this.onChangeInputNumber.bind(this)
    this.onChangeCheckboxIsUniqueLabeled = this.onChangeCheckboxIsUniqueLabeled.bind(this)
    this.onChangeInputRemark = this.onChangeInputRemark.bind(this)
    this.onSubmit = this.onSubmit.bind(this)

    // Register submit event
    if (_.isFunction(this.props.registerSubmit)) {
      this.props.registerSubmit(this.onSubmit)
    }
  }

  getInitialInput(props = this.props) {
    return _.extend(
      {
        location: (props.data && props.data.location && props.data.location.id) || null,
        islabeled: (props.data && props.data.islabeled) || false,
        compartment: (props.data && props.data.compartment) || '',
        number: props.withNumber
          ? (props.data && props.data.number) || ''
          : null,
        isuniquelabeled: props.withNumber
          ? (props.data && props.data.isuniquelabeled) || false
          : null,
        remark: (props.data && props.data.remark) || ''
      },
      props.data
        ? {}
        : {
            quantity: 1
          }
    )
  }

  setInput(input = {}) {
    this.setState((prevState, props) => ({
      input: _.extend(
        {},
        prevState.input,
        input
      )
    }))
  }

  componentDidUpdate(prevProps, prevState) {
    // When async fetch finished
    if (prevProps.isLoading && !this.props.isLoading) {
      this.setState({
        input: this.getInitialInput()
      })
    }
  }

  onChangeInputQuantity(quantity) {
    this.setInput({ quantity })
  }

  onChangeInputLocation(location) {
    this.setInput({ location })
    if (_.isFunction(this.props.fetchCompartments)) {
      this.props.fetchCompartments(location)
    }
  }

  onChangeCheckboxIsLabeled(islabeled) {
    this.setInput({ islabeled })
  }

  onChangeInputCompartment(compartment) {
    this.setInput({ compartment })
  }

  onChangeInputNumber(number) {
    this.setInput({ number })
  }

  onChangeCheckboxIsUniqueLabeled(isuniquelabeled) {
    this.setInput({ isuniquelabeled })
  }

  onChangeInputRemark(remark) {
    this.setInput({ remark })
  }

  onSubmit() {
    // Check that fields have been filled
    const formErrors = []
    if (_.isNull(this.state.input.location)) {
      formErrors.push(ERROR.EMPTY_LOCATION)
    }
    if (!this.props.data) {
      if (!_.isNumber(this.state.input.quantity) || Number(this.state.input.quantity) < 1 || String(this.state.input.quantity).length === 0) {
        formErrors.push(ERROR.EMPTY_QUANTITY)
      }
    }
    if (this.props.withNumber) {
      if (String(this.state.input.number).length === 0) {
        formErrors.push(ERROR.EMPTY_NUMBER)
      }
    }
    this.setState({
      formErrors
    })
    if (!_.isEmpty(formErrors)) {
      return;
    }

    // Build and submit correctly formatted input
    const input = _.extend(
      _.pick(this.state.input, 'islabeled', 'compartment', 'remark'),
      {
        location: {
          id: this.state.input.location
        }
      },
      !this.props.data
        ? _.pick(this.state.input, 'quantity')
        : {},
      !this.props.data
        ? {
            product: _.pick(this.props.product, 'id')
          }
        : {},
      this.props.withNumber
        ? _.pick(this.state.input, 'number', 'isuniquelabeled')
        : {}
    )
    this.props.onSubmit(input)
  }

  render() {
    return (
      <React.Fragment>
        {
          this.props.error
            ? <Dismissible
                header={AppUtils.errorToTitle(this.props.error)}
                content={AppUtils.errorToText(this.props.error)}
                color='red'
                onDismiss={this.props.onClearError}
              />
            : null
        }
        {
          !_.isEmpty(this.state.formErrors)
            ? <Dismissible
                content='Veuillez résoudre les problèmes ci-dessous avant de poursuivre.'
                color='orange'
              />
            : null
        }
        <Form
          onSubmit={this.onSubmit}
          loading={this.props.isLoading || this.props.isSaving}
        >
          <StaticInput
            label='Matériel'
            content={this.props.product.name}
          />
          {
            !this.props.data && !this.props.withNumber
              ? <IntegerInput
                  label='Quantité'
                  value={this.state.input.quantity}
                  onChange={this.onChangeInputQuantity}
                  error={
                    _.contains(this.state.formErrors, ERROR.EMPTY_QUANTITY)
                      ? ERROR.EMPTY_QUANTITY
                      : null
                  }
                />
              : null
          }
          <LocationSelect
            label='Emplacement'
            loading={this.props.locationsIsFetching}
            options={this.props.locationsList}
            selected={this.state.input.location}
            onChange={this.onChangeInputLocation}
            error={
              _.contains(this.state.formErrors, ERROR.EMPTY_LOCATION)
                ? ERROR.EMPTY_LOCATION
                : null
            }
          />
          <CheckboxInput
            label=''
            text='Etiquetté correctement'
            checked={this.state.input.islabeled}
            onChange={this.onChangeCheckboxIsLabeled}
            smallPadding={true}
          />
          <TextInput
            label='Compartiment'
            placeholder='Arrière'
            value={this.state.input.compartment}
            onChange={this.onChangeInputCompartment}
            suggestions={this.props.compartmentsList}
            suggestionsLoading={this.props.compartmentsIsFetching}
          />
          {
            this.props.withNumber
              ? <React.Fragment>
                  <TextInput
                    label='N°'
                    placeholder='25'
                    value={this.state.input.number}
                    onChange={this.onChangeInputNumber}
                    error={
                      _.contains(this.state.formErrors, ERROR.EMPTY_NUMBER)
                        ? ERROR.EMPTY_NUMBER
                        : null
                    }
                  />
                  <CheckboxInput
                    label=''
                    text='Etiquetté correctement'
                    checked={this.state.input.isuniquelabeled}
                    onChange={this.onChangeCheckboxIsUniqueLabeled}
                    smallPadding={true}
                  />
                </React.Fragment>
              : null
          }
          <TextInput
            label='Remarque'
            placeholder='...'
            value={this.state.input.remark}
            onChange={this.onChangeInputRemark}
            multiline={true}
          />
        </Form>
      </React.Fragment>
    )
  }
}

ItemForm.propTypes = {
  data: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onClearError: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  registerSubmit: PropTypes.func,
  error: PropTypes.object,
  product: PropTypes.object,
  withNumber: PropTypes.bool.isRequired,
  locationsList: PropTypes.array,
  locationsIsFetching: PropTypes.bool,
  fetchCompartments: PropTypes.func.isRequired,
  compartmentsList: PropTypes.array,
  compartmentsIsFetching: PropTypes.bool
}

export default ItemForm



// WEBPACK FOOTER //
// ./src/components/forms/ItemForm.js